<template>

<!--
	The leaderboard: three category tabs (Points / Streaks / One-Shots), each with
	a row of chips for its sub-boards. The active chip resolves to a server scope
	(api.request('leaderboard', { scope })); changing tab or chip refetches. Rows
	are { rank, account:{id,name,avatar}, value, meta } — `meta` is a small line
	under the name (plays count, streak dates, ...). The signed-in player's own row
	is highlighted. Chrome (Topbar / Foot) wraps the table.
-->

<div class="leaderboard">

	<app-topbar v-on:signin="$emit('signin')" />

	<main class="leaderboard-body">

		<div class="leaderboard-card">

			<h1 class="leaderboard-title">Leaderboard</h1>

			<!-- primary: the three categories -->
			<div class="leaderboard-tabs" role="tablist">
				<button
					v-for="cat in categories"
					:key="cat.key"
					type="button"
					class="leaderboard-tab"
					:class="{ 'is-current': cat.key === category }"
					role="tab"
					:aria-selected="cat.key === category ? 'true' : 'false'"
					v-on:click="selectCategory(cat.key)"
				>{{ cat.label }}</button>
			</div>

			<!-- secondary: the active category's sub-boards -->
			<div class="leaderboard-chips" role="tablist">
				<button
					v-for="s in subs"
					:key="s.key"
					type="button"
					class="leaderboard-chip"
					:class="{ 'is-current': s.key === sub }"
					role="tab"
					:aria-selected="s.key === sub ? 'true' : 'false'"
					v-on:click="selectSub(s.key)"
				>{{ s.label }}</button>
			</div>

			<div class="leaderboard-head">
				<span class="leaderboard-head-label">Player</span>
				<span class="leaderboard-head-label">{{ valueLabel }}</span>
			</div>

			<div v-if="loading" class="leaderboard-state">Loading…</div>

			<div v-else-if="!rows.length" class="lb-empty">
				<app-mark :size="42" />
				<div class="lb-empty-head">No scores yet</div>
				<p class="lb-empty-sub">{{ emptyText }}</p>
				<router-link to="/" class="lb-empty-cta">Play today’s puzzle</router-link>
			</div>

			<div v-else class="leaderboard-rows">
				<div
					v-for="(row, index) in rows"
					:key="row.rank + '-' + index"
					class="leaderboard-row"
					:class="{ 'is-me': isMe(row) }"
				>
					<span class="leaderboard-rank">{{ row.rank }}</span>
					<span class="leaderboard-avatar" :style="{ backgroundColor: avatarBg(row) }">
						<img
							v-if="hasAvatar(row) && !failed[row.rank]"
							class="leaderboard-avatar-img"
							:src="row.account.avatar"
							alt=""
							referrerpolicy="no-referrer"
							v-on:error="onAvatarError(row)"
						>
						<span v-else class="leaderboard-initial">{{ initial(row) }}</span>
					</span>
					<span class="leaderboard-player">
						<span class="leaderboard-name">{{ rowName(row) }}</span>
						<span v-if="row.meta" class="leaderboard-meta">{{ row.meta }}</span>
					</span>
					<span class="leaderboard-value">{{ formatValue(row.value) }}</span>
				</div>
			</div>

		</div>

	</main>

	<app-foot />

</div>

</template>

<script>

import api from '../service/api'
import util from '../service/util'

export default {

	name: 'Leaderboard',

	data: function() {

		return {
			// three categories, each with its sub-boards (chips). `value` is the
			// value-column label; `scope` is the server scope for that board.
			categories: [
				{
					key: 'points',
					label: 'Points',
					subs: [
						{ key: 'today',   label: 'Today',    scope: 'points.today',   value: 'Points' },
						{ key: 'week',    label: 'Week',     scope: 'points.week',    value: 'Points' },
						{ key: 'month',   label: 'Month',    scope: 'points.month',   value: 'Points' },
						{ key: 'year',    label: 'Year',     scope: 'points.year',    value: 'Points' },
						{ key: 'alltime', label: 'All-time', scope: 'points.alltime', value: 'Points' }
					]
				},
				{
					key: 'streaks',
					label: 'Streaks',
					subs: [
						{ key: 'active', label: 'Active', scope: 'streaks.active', value: 'Days' },
						{ key: 'best',   label: 'Best',   scope: 'streaks.best',   value: 'Days' }
					]
				},
				{
					key: 'oneshot',
					label: 'One-Shots',
					subs: [
						{ key: 'total',  label: 'Total',  scope: 'oneshot.total',  value: 'One-shots' },
						{ key: 'active', label: 'Active', scope: 'oneshot.active', value: 'Days' },
						{ key: 'best',   label: 'Best',   scope: 'oneshot.best',   value: 'Days' }
					]
				}
			],
			category: 'points',
			sub: 'today',
			rows: [],
			loading: false,
			// per-rank flag set when a row's avatar image fails (ORB-blocked, 404)
			// so it falls back to the coloured initial
			failed: {}
		}

	},

	computed: {

		account: function() {

			return this.$store.getters['account/account']

		},

		// the active category object
		currentCategory: function() {

			var key = this.category

			return this.categories.find(function(c) { return c.key === key }) || this.categories[0]

		},

		// the active category's sub-boards (the chip row)
		subs: function() {

			return this.currentCategory.subs

		},

		// the active sub-board object
		currentSub: function() {

			var key = this.sub
			var match = this.subs.find(function(s) { return s.key === key })

			return match || this.subs[0]

		},

		valueLabel: function() {

			return this.currentSub.value

		},

		// a friendly line under the empty-state heading, tuned to the category
		emptyText: function() {

			if (this.category === 'streaks') return 'Solve the puzzle day after day to build a streak. The longest runs land here.'

			if (this.category === 'oneshot') return 'Nail the box art on your very first guess. One-shot feats land here.'

			return 'Points add up across every puzzle you play. Keep solving to climb the board.'

		}

	},

	created: function() {

		this.fetch()

	},

	methods: {

		// switch category: jump to its first sub-board, then refetch
		selectCategory: function(key) {

			if (key === this.category) return

			this.category = key
			this.sub = this.currentCategory.subs[0].key
			this.fetch()

		},

		// switch sub-board within the current category
		selectSub: function(key) {

			if (key === this.sub) return

			this.sub = key
			this.fetch()

		},

		fetch: function() {

			var self = this
			var scope = this.currentSub.scope

			this.loading = true

			return api.request('leaderboard', { scope: scope }).then(function(json) {

				// a stale board's response (the player flicked on) must not
				// overwrite the current one
				if (scope !== self.currentSub.scope) return

				self.loading = false
				self.rows = (json && json.rows) || []
				// give avatars a fresh chance to load on the new board
				self.failed = {}

			})

		},

		// thousands separators (matters for points; harmless for small counts)
		formatValue: function(value) {

			var n = Number(value) || 0

			return n.toLocaleString('en-US')

		},

		rowName: function(row) {

			return (row.account && row.account.name) || 'Player'

		},

		hasAvatar: function(row) {

			return !!(row.account && row.account.avatar)

		},

		// the colour behind the initial (seen when there's no avatar, or it failed)
		avatarBg: function(row) {

			return util.avatarColor(this.initial(row))

		},

		// an avatar image failed to load -> fall back to the coloured initial
		onAvatarError: function(row) {

			this.$set(this.failed, row.rank, true)

		},

		initial: function(row) {

			var name = this.rowName(row)

			return name ? name.charAt(0).toUpperCase() : '?'

		},

		isMe: function(row) {

			if (!this.account) return false

			var mine = row.account && row.account.id

			return mine != null && mine === this.account.id

		}

	}

}

</script>

<style scoped>

.leaderboard {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	flex: 1;
}

.leaderboard-body {
	flex: 1;
	display: flex;
	justify-content: center;
	padding: 20px 16px;
}

.leaderboard-card {
	width: 100%;
	max-width: 480px;
}

.leaderboard-title {
	font-family: var(--font-display);
	font-size: 26px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0 0 16px;
	color: var(--ink);
}

.leaderboard-tabs {
	display: flex;
	border-bottom: 1px solid var(--hairline);
}

.leaderboard-tab {
	flex: 1;
	text-align: center;
	white-space: nowrap;
	background: none;
	border: none;
	padding: 0 0 11px;
	margin-bottom: -1px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	cursor: pointer;
	border-bottom: 2px solid transparent;
}

.leaderboard-tab:hover {
	color: var(--ink);
}

.leaderboard-tab.is-current {
	color: var(--ink);
	font-weight: 600;
	border-bottom-color: var(--ink);
}

/* secondary sub-board chips */
.leaderboard-chips {
	display: flex;
	justify-content: center;
	gap: 6px;
	margin: 12px 0 2px;
	overflow-x: auto;
}

.leaderboard-chip {
	flex: none;
	padding: 5px 12px;
	border-radius: 999px;
	border: 1px solid var(--hairline);
	background: transparent;
	font-family: var(--font-mono);
	font-size: 11px;
	letter-spacing: 0.04em;
	color: var(--muted);
	cursor: pointer;
	white-space: nowrap;
	transition: color 140ms ease, border-color 140ms ease, background 140ms ease;
}

.leaderboard-chip:hover {
	color: var(--ink);
	border-color: var(--muted-2);
}

.leaderboard-chip.is-current {
	background: var(--ink);
	border-color: var(--ink);
	color: var(--paper);
}

.leaderboard-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 2px 10px;
}

.leaderboard-head-label {
	font-family: var(--font-mono);
	font-size: 10px;
	letter-spacing: 0.1em;
	color: var(--muted);
	text-transform: uppercase;
}

.leaderboard-state {
	padding: 28px 2px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	text-align: center;
}

/* empty state: mark + heading + tuned blurb + CTA back to today's puzzle */
.lb-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 40px 20px 36px;
}

.lb-empty >>> .mark,
.lb-empty >>> svg {
	opacity: 0.9;
}

.lb-empty-head {
	margin-top: 16px;
	font-family: var(--font-display);
	font-size: 20px;
	font-weight: 600;
	color: var(--ink);
}

.lb-empty-sub {
	margin: 7px auto 0;
	max-width: 300px;
	font-family: var(--font-body);
	font-size: 13.5px;
	line-height: 1.5;
	color: var(--muted-2);
}

.lb-empty-cta {
	margin-top: 18px;
	display: inline-flex;
	align-items: center;
	height: 42px;
	padding: 0 22px;
	border-radius: 8px;
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
}

.lb-empty-cta:hover {
	background: var(--accent);
}

.leaderboard-rows {
	display: flex;
	flex-direction: column;
}

.leaderboard-row {
	position: relative;
	display: flex;
	align-items: center;
	gap: 14px;
	padding: 11px 12px;
	border-top: 1px solid #efe9dc;
	border-radius: 6px;
}

.leaderboard-row.is-me {
	background: #eef1f6;
}

.leaderboard-row.is-me::before {
	content: '';
	position: absolute;
	left: 0;
	top: 6px;
	bottom: 6px;
	width: 3px;
	background: var(--accent);
	border-radius: 3px;
}

.leaderboard-rank {
	font-family: var(--font-mono);
	font-size: 14px;
	color: var(--muted);
	width: 20px;
	flex: none;
}

.leaderboard-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background-color: #ece5d7;
	border: 1px solid #e0d8c8;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	overflow: hidden;
}

.leaderboard-avatar-img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

.leaderboard-initial {
	font-family: var(--font-display);
	font-weight: 600;
	font-size: 15px;
	color: #fff;
}

/* name + meta stack */
.leaderboard-player {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 1px;
}

.leaderboard-name {
	font-family: var(--font-body);
	font-size: 15px;
	color: var(--ink);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.leaderboard-meta {
	font-family: var(--font-mono);
	font-size: 10.5px;
	letter-spacing: 0.01em;
	color: var(--muted);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.leaderboard-value {
	font-family: var(--font-mono);
	font-size: 16px;
	color: var(--ink);
	flex: none;
}

/* MOBILE: the card runs the full screen width so the tab + row dividers reach
   both edges; the text is re-inset, and the category tabs are equal-width. */
@media (max-width: 599px) {

	.leaderboard-body {
		padding-left: 0;
		padding-right: 0;
	}

	.leaderboard-title {
		padding: 0 16px;
		text-align: center;
	}

	/* tabs are already equal-width (base); just tighten the type a touch */
	.leaderboard-tab {
		font-size: 13px;
	}

	/* chips + header inset from the screen edges */
	.leaderboard-chips {
		padding: 0 16px;
	}

	.leaderboard-head {
		padding-left: 16px;
		padding-right: 16px;
	}

	.leaderboard-state {
		padding-left: 16px;
		padding-right: 16px;
	}

}

</style>
